/**
 * MOOJO — Cart V2 · champ TVA B2B
 * -----------------------------------------------------------------------------
 * Persiste un numéro de TVA optionnel en attribut de panier (`vat_number`),
 * lu ensuite sur Order.customAttributes / note_attributes (Billit).
 *
 * Lot TVA2-b (2026-10-23) :
 *   - Sur input : nettoyage en mémoire sans réécriture ni règle des 9 chiffres.
 *     Valide → bordure verte + coche, statut « enregistré : {valeur} »,
 *     persistance après 400 ms. Invalide → aucun statut, aucun blocage.
 *   - Sur change/blur/submit : réécriture, règle des 9 chiffres, contrôle ;
 *     invalide → message en couleur + bouton caisse grisé (disabled).
 * Belgique (chiffre de contrôle), UE, GR→EL, CHE, GB. Pas d'appel VIES.
 * Champ vide = aucun blocage. Les paiements express contournent le panier (lot C).
 */
(function () {
  'use strict';

  var NAME_PATTERN = /^attributes\[(.+)\]$/;
  var LIVE_PERSIST_MS = 400;

  var EU_EXACT = {
    NL: /^NL\d{9}B\d{2}$/,
    FR: /^FR[0-9A-Z]{2}\d{9}$/,
    DE: /^DE\d{9}$/,
    LU: /^LU\d{8}$/,
    AT: /^ATU\d{8}$/,
    ES: /^ES[0-9A-Z]\d{7}[0-9A-Z]$/,
    IT: /^IT\d{11}$/,
    DK: /^DK\d{8}$/,
    PT: /^PT\d{9}$/,
    EL: /^EL\d{9}$/
  };

  var EU_PREFIXES =
    /^(AT|BE|BG|CY|CZ|DE|DK|EE|EL|ES|FI|FR|GR|HR|HU|IE|IT|LT|LU|LV|MT|NL|PL|PT|RO|SE|SI|SK)$/;

  function cartUpdateUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'cart/update.js';
  }

  /**
   * Nettoyage complet (change/blur/submit) :
   *   1. majuscules ; retirer espaces, points, tirets, barres obliques ;
   *   2. GR → EL (alias VIES) ;
   *   3. 10 chiffres commençant par 0 ou 1 → préfixer BE ;
   *   4. 9 chiffres seuls (ou BE + 9 chiffres) → préfixer BE0 ;
   *   5. la valeur nettoyée est réaffichée par l'appelant.
   */
  function cleanVat(value) {
    return cleanVatCore(value, true);
  }

  /** Nettoyage live (input) : sans la règle des 9 chiffres → BE0. */
  function cleanVatLive(value) {
    return cleanVatCore(value, false);
  }

  function cleanVatCore(value, applyNineDigitRule) {
    if (value === null || value === undefined) return '';
    var s = String(value)
      .toUpperCase()
      .replace(/[\s.\-\/]/g, '');
    if (!s) return '';

    if (s.indexOf('CHE') === 0) return s;
    if (s.indexOf('GB') === 0) return s;

    if (s.indexOf('GR') === 0 && s.length > 2) {
      s = 'EL' + s.slice(2);
    }

    var m = /^([A-Z]{2})(.*)$/.exec(s);
    if (m && EU_PREFIXES.test(m[1])) {
      var prefix = m[1];
      var rest = m[2];
      if (prefix === 'BE') {
        if (applyNineDigitRule && /^\d{9}$/.test(rest)) return 'BE0' + rest;
        return 'BE' + rest;
      }
      if (prefix === 'GR') return 'EL' + rest;
      return prefix + rest;
    }

    if (/^[01]\d{9}$/.test(s)) return 'BE' + s;
    if (applyNineDigitRule && /^\d{9}$/.test(s)) return 'BE0' + s;
    return s;
  }

  function beCheckDigitOk(vat) {
    if (!/^BE[01]\d{9}$/.test(vat)) return false;
    var digits = vat.slice(2);
    var first8 = parseInt(digits.slice(0, 8), 10);
    var last2 = parseInt(digits.slice(8, 10), 10);
    return 97 - (first8 % 97) === last2;
  }

  function isValidVat(vat) {
    if (!vat) return true;
    if (/^CHE\d{9}(MWST|TVA|IVA)?$/.test(vat)) return true;
    if (/^GB(\d{9}|\d{12})$/.test(vat)) return true;
    if (/^BE/.test(vat)) return beCheckDigitOk(vat);

    var prefix = vat.slice(0, 2);
    if (EU_EXACT[prefix]) return EU_EXACT[prefix].test(vat);

    if (EU_PREFIXES.test(prefix)) {
      return /^[A-Z]{2}[0-9A-Z]{8,12}$/.test(vat);
    }

    return false;
  }

  function fieldFromEventTarget(target) {
    if (!target) return null;
    if (NAME_PATTERN.test(target.getAttribute('name') || '')) return target;
    return target.closest ? target.closest('[name^="attributes["]') : null;
  }

  var CartAttribute = class extends HTMLElement {
    constructor() {
      super();
      this.lastPersistedValue = undefined;
      this._form = null;
      this._abort = null;
      this._persistSeq = 0;
      this._invalid = false;
      this._liveTimer = null;
      this.onFieldChanged = this.onFieldChanged.bind(this);
      this.onFieldInput = this.onFieldInput.bind(this);
      this.onFieldBlur = this.onFieldBlur.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onRetry = this.onRetry.bind(this);
    }

    connectedCallback() {
      this.addEventListener('change', this.onFieldChanged);
      this.addEventListener('input', this.onFieldInput);
      this._form = this.resolveForm();
      if (this._form) this._form.addEventListener('submit', this.onFormSubmit);

      var retry = this.querySelector('[data-vat-retry]');
      if (retry) retry.addEventListener('click', this.onRetry);

      var field = this.querySelector('[name^="attributes["]');
      if (field) {
        field.addEventListener('blur', this.onFieldBlur);
        // Valeur déjà en panier : un seul passage de nettoyage à l'ouverture.
        var cleaned = cleanVat(field.value);
        if (field.value !== cleaned) field.value = cleaned;
        this.lastPersistedValue = cleaned;
        if (!cleaned) {
          this._invalid = false;
          this.setStatus('default', '');
          this.setCheckoutBlocked(false);
        } else if (isValidVat(cleaned)) {
          this._invalid = false;
          this.setStatus('saved', this.formatSaved(cleaned));
          this.setCheckoutBlocked(false);
        } else {
          this._invalid = true;
          this.setStatus('invalid', this.dataset.invalid);
          this.setCheckoutBlocked(true);
        }
      }
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onFieldChanged);
      this.removeEventListener('input', this.onFieldInput);
      if (this._form) this._form.removeEventListener('submit', this.onFormSubmit);
      var retry = this.querySelector('[data-vat-retry]');
      if (retry) retry.removeEventListener('click', this.onRetry);
      var field = this.querySelector('[name^="attributes["]');
      if (field) field.removeEventListener('blur', this.onFieldBlur);
      if (this._liveTimer) {
        clearTimeout(this._liveTimer);
        this._liveTimer = null;
      }
      if (this._abort) this._abort.abort();
      this.setCheckoutBlocked(false);
    }

    resolveForm() {
      var nested = this.closest('form');
      if (nested) return nested;
      var field = this.querySelector('[name^="attributes["]');
      var formId = field && field.getAttribute('form');
      return formId ? document.getElementById(formId) : null;
    }

    formatSaved(cleaned) {
      var tpl = this.dataset.savedValue || '';
      if (tpl) {
        return tpl
          .replace(/§VALUE§/g, cleaned)
          .replace(/\{\{\s*value\s*\}\}/g, cleaned);
      }
      var base = this.dataset.saved || '';
      return base ? base + ' : ' + cleaned : cleaned;
    }

    setCheckoutBlocked(blocked) {
      var buttons = document.querySelectorAll('button[name="checkout"], input[name="checkout"]');
      for (var i = 0; i < buttons.length; i++) {
        var btn = buttons[i];
        if (blocked) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('data-vat-blocked', 'true');
          btn.classList.add('vat-checkout--blocked');
        } else if (btn.getAttribute('data-vat-blocked') === 'true') {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
          btn.removeAttribute('data-vat-blocked');
          btn.classList.remove('vat-checkout--blocked');
        }
      }
    }

    setStatus(state, message) {
      this.dataset.state = state || 'default';
      var status = this.querySelector('[data-vat-status]');
      var retry = this.querySelector('[data-vat-retry]');
      var field = this.querySelector('[name^="attributes["]');

      if (status) {
        status.textContent = message || '';
        status.classList.toggle('vat-field__status--saving', state === 'saving');
        status.classList.toggle('vat-field__status--saved', state === 'saved');
        status.classList.toggle('vat-field__status--error', state === 'error' || state === 'invalid');
      }
      if (retry) retry.hidden = state !== 'error';
      if (field) {
        if (state === 'error' || state === 'invalid') field.setAttribute('aria-invalid', 'true');
        else field.removeAttribute('aria-invalid');
      }
    }

    applyCleanAndValidate(field) {
      var cleaned = cleanVat(field.value);
      if (field.value !== cleaned) field.value = cleaned;

      if (!cleaned) {
        this._invalid = false;
        this.setCheckoutBlocked(false);
        return { cleaned: '', valid: true };
      }

      var valid = isValidVat(cleaned);
      this._invalid = !valid;
      this.setCheckoutBlocked(!valid);
      return { cleaned: cleaned, valid: valid };
    }

    clearLiveTimer() {
      if (this._liveTimer) {
        clearTimeout(this._liveTimer);
        this._liveTimer = null;
      }
    }

    /**
     * Pendant la frappe : nettoyage en mémoire seulement (sans règle 9 chiffres),
     * sans réécriture du champ. Valide → UI confirmation + persist 400 ms.
     * Invalide → aucun statut, aucun blocage.
     */
    onFieldInput(event) {
      var field = fieldFromEventTarget(event.target);
      if (!field) return;

      this.clearLiveTimer();

      if (this._invalid) {
        this._invalid = false;
        this.setCheckoutBlocked(false);
      }

      var live = cleanVatLive(field.value);
      if (!live) {
        this.setStatus('default', '');
        return;
      }

      if (isValidVat(live)) {
        this.setStatus('saved', this.formatSaved(live));
        var self = this;
        this._liveTimer = setTimeout(function () {
          self._liveTimer = null;
          self.persistCleanedValue(field, live, { rewrite: false, quiet: true });
        }, LIVE_PERSIST_MS);
        return;
      }

      this.setStatus('default', '');
    }

    onFieldChanged(event) {
      var field = fieldFromEventTarget(event.target);
      if (!field) return;
      this.clearLiveTimer();
      this.persistField(field);
    }

    onFieldBlur(event) {
      var field = event.target;
      if (!field || !NAME_PATTERN.test(field.getAttribute('name') || '')) return;
      this.clearLiveTimer();
      // `change` part déjà au blur si la valeur a bougé. On repasse si le
      // texte affiché n'est pas encore nettoyé (ex. frappe puis clic ailleurs).
      var raw = field.value;
      var cleaned = cleanVat(raw);
      if (raw !== cleaned || cleaned !== (this.lastPersistedValue || '')) {
        this.persistField(field);
      } else if (cleaned && !isValidVat(cleaned)) {
        this._invalid = true;
        this.setCheckoutBlocked(true);
        this.setStatus('invalid', this.dataset.invalid);
      }
    }

    onFormSubmit(event) {
      var field = this.querySelector('[name^="attributes["]');
      if (!field) return;
      this.clearLiveTimer();
      var result = this.applyCleanAndValidate(field);
      if (!result.valid) {
        event.preventDefault();
        event.stopPropagation();
        this.setStatus('invalid', this.dataset.invalid);
        return;
      }
      this.persistField(field, { keepalive: true });
    }

    onRetry(event) {
      event.preventDefault();
      var field = this.querySelector('[name^="attributes["]');
      if (field) this.persistField(field);
    }

    persistField(field, options) {
      var match = NAME_PATTERN.exec((field && field.getAttribute('name')) || '');
      if (!match) return;

      options = options || {};
      var result = this.applyCleanAndValidate(field);
      this.persistCleanedValue(field, result.cleaned, {
        keepalive: options.keepalive,
        rewrite: true,
        valid: result.valid
      });
    }

    persistCleanedValue(field, cleaned, options) {
      var match = NAME_PATTERN.exec((field && field.getAttribute('name')) || '');
      if (!match) return;

      var key = match[1];
      options = options || {};
      var valid = options.valid !== undefined ? options.valid : isValidVat(cleaned);

      if (options.rewrite && field && field.value !== cleaned) {
        field.value = cleaned;
      }

      if (!valid) {
        this._invalid = true;
        this.setCheckoutBlocked(true);
        this.setStatus('invalid', this.dataset.invalid);
        if (this.lastPersistedValue) {
          this._writeAttribute(key, '', options, true);
        }
        return;
      }

      this._invalid = false;
      this.setCheckoutBlocked(false);

      if (cleaned === this.lastPersistedValue && this.dataset.state !== 'error') {
        if (cleaned) this.setStatus('saved', this.formatSaved(cleaned));
        else this.setStatus('default', '');
        return;
      }

      this._writeAttribute(key, cleaned, options, false);
    }

    _writeAttribute(key, value, options, fromInvalidClear) {
      if (this._abort) this._abort.abort();
      this._abort = !options.keepalive && typeof AbortController === 'function' ? new AbortController() : null;
      var seq = ++this._persistSeq;
      var self = this;

      if (fromInvalidClear) {
        /* statut déjà « invalid » */
      } else if (value) {
        if (!options.quiet) this.setStatus('saving', this.dataset.saving);
        else this.setStatus('saved', this.formatSaved(value));
      } else {
        this.setStatus('default', '');
      }

      var attributes = {};
      attributes[key] = value;

      var fetchOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes }),
        keepalive: !!options.keepalive
      };
      if (this._abort) fetchOptions.signal = this._abort.signal;

      fetch(cartUpdateUrl(), fetchOptions)
        .then(function (res) {
          if (!res.ok) throw new Error('http');
          return res.json();
        })
        .then(function () {
          if (seq !== self._persistSeq) return;
          self.lastPersistedValue = value;
          if (fromInvalidClear || self._invalid) {
            self.setStatus('invalid', self.dataset.invalid);
            self.setCheckoutBlocked(true);
          } else if (value === '') {
            self.setStatus('default', '');
          } else {
            self.setStatus('saved', self.formatSaved(value));
          }
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (seq !== self._persistSeq) return;
          self.lastPersistedValue = undefined;
          if (fromInvalidClear || self._invalid) {
            self.setStatus('invalid', self.dataset.invalid);
            self.setCheckoutBlocked(true);
          } else {
            self.setStatus('error', self.dataset.error);
          }
        });
    }
  };

  if (!window.customElements.get('cart-attribute')) {
    window.customElements.define('cart-attribute', CartAttribute);
  }

  window.MoojoVatField = {
    cleanVat: cleanVat,
    cleanVatLive: cleanVatLive,
    isValidVat: isValidVat,
    beCheckDigitOk: beCheckDigitOk
  };
})();
